/*
 * OneOdio checkout guard (performance-optimized)
 * ----------------------------------------------
 * Goal: keep checkout reliable WITHOUT the page-wide, unthrottled MutationObserver
 * that previously re-scanned the whole document on every DOM change (the main cause
 * of mobile jank). This version is event-driven and only ever inspects the cart
 * drawer subtree, coalescing bursts into a single pass per animation frame.
 */
(() => {
  const DIRECT_SELECTOR = '[data-direct-checkout-trigger], .product-form__direct-checkout';
  const SAFE_ADD_LABEL = 'Add To Cart';

  // Strip custom "fast checkout / buy now" triggers that were error-prone on mobile.
  // (Native Shopify dynamic checkout / Shop Pay buttons are NOT touched.)
  const neutralizeDirectCheckout = (root) => {
    root.querySelectorAll(DIRECT_SELECTOR).forEach((element) => {
      element.removeAttribute('data-direct-checkout-trigger');
      element.classList.remove('product-form__direct-checkout');
      if (/fast checkout|buy now/i.test(element.textContent || '')) {
        element.textContent = SAFE_ADD_LABEL;
      }
    });
  };

  // Replace the cart's submit <button name="checkout"> with a plain link so checkout
  // is an instant, dependency-free navigation (no JS handler can stall or error it).
  const stabilizeCartCheckout = (root) => {
    root.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((button) => {
      const inCart = button.closest('#CartDrawer, .template-cart, main, .cart');
      if (!inCart) return;

      const link = document.createElement('a');
      link.href = '/checkout';
      link.className = button.className || 'button button--primary';
      link.setAttribute('data-no-instant', '');
      link.dataset.oneodioCheckoutStable = 'true';
      link.innerHTML = button.innerHTML || button.value || 'Check out';
      button.replaceWith(link);
    });
  };

  const hideCheckoutFriction = (root) => {
    root.querySelectorAll('form[is="cart-discount"], .cart-discount').forEach((element) => {
      if (element.hidden) return;
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
    });
  };

  // Only prevent instant-page prefetch on checkout links. We deliberately do NOT
  // rewrite live "/checkouts/..." session URLs (doing so can break an active checkout).
  const markCheckoutLinks = (root) => {
    root.querySelectorAll('a[href="/checkout"], a[href$="/checkout"]').forEach((link) => {
      if (!link.hasAttribute('data-no-instant')) link.setAttribute('data-no-instant', '');
    });
  };

  const run = () => {
    neutralizeDirectCheckout(document);
    stabilizeCartCheckout(document);
    hideCheckoutFriction(document);
    markCheckoutLinks(document);
  };

  // Coalesce any number of triggers into one pass per frame.
  let scheduled = false;
  const scheduleRun = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  };

  // Cheap, event-driven guard for custom direct-checkout triggers (capture phase).
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest(DIRECT_SELECTOR) : null;
    if (!target) return;
    target.removeAttribute('data-direct-checkout-trigger');
    target.classList.remove('product-form__direct-checkout');
  }, true);

  const init = () => {
    run();

    // Re-apply only when the cart actually changes (covers drawer + cart page re-render).
    document.addEventListener('cart:updated', scheduleRun);
    document.addEventListener('cart:refresh', scheduleRun);

    // Safety net: observe ONLY the cart drawer subtree (small, mutates rarely),
    // never the whole document. rAF-coalesced so a burst costs a single pass.
    const drawer = document.getElementById('CartDrawer');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(scheduleRun).observe(drawer, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
